// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/Sale.tsx
// 
// تغییرات این نسخه:
// - 🆕 فیلد شماره موتر (truck_plate) در فرم ثبت و ویرایش
// - 🆕 نمایش شماره موتر کنار سریال در لیست
// - 🆕 پشتیبانی از اعداد اعشاری در تعداد (مثل 0.8 یا .08)
//   quantity حالا string است تا تایپ کردن اعشاری روان باشد
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, customersAPI, warehouseAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, Eye, Printer, Edit2 } from "lucide-react";
import { printSaleInvoice } from "../../../utils/InvoicePrint";
import {
  inputSt, rowSt, badgeSt, addBtnSt, iconBtnSt,
  Lbl, BtnCancel, BtnSave, Toast, Loading, Empty, Modal,
} from "./Purchase";
import JournalFilter, { emptyFilter, applyFilter, JournalFilterValues, exportToExcel } from "./JournalFilter";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

// 🆕 helper برای محاسبه total_price با پشتیبانی string
const calcTotal = (qty: any, price: any) => {
  const q = parseFloat(String(qty)) || 0;
  const p = parseFloat(String(price)) || 0;
  return q * p;
};

interface Item {
  product_id: string; product_name: string; unit: string;
  quantity: string; // 🆕 string برای پشتیبانی از .08, 0., 0.8
  warehouse_id: string; warehouse_name: string;
  unit_price: string; // 🆕 string هم
  total_price: number;
}

interface ProductOption {
  value: string;
  label: string;
  product_id: number;
  product_name: string;
  warehouse_id: number;
  warehouse_name: string;
  unit: string;
  item_type: "product" | "service";
}

export default function Sale() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [list, setList]                   = useState<any[]>([]);
  const [loading, setLoading]             = useState(true);
  const [showForm, setShowForm]           = useState(false);
  const [editItem, setEditItem]           = useState<any>(null);
  const [viewItem, setViewItem]           = useState<any>(null);
  const [customers, setCustomers]         = useState<any[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [filter, setFilter]               = useState<JournalFilterValues>(emptyFilter);
  const [toast, setToast]                 = useState({ text: "", ok: true });
  const [statusFilter, setStatusFilter] = useState<"all"|"pending"|"confirmed">("all");

  const [form, setForm] = useState({
    date: todayGregorian(), customer_id: "", customer_currency: "USD",
    discount: "0", notes: "", attachment: "", truck_plate: "", waybill_number: "",
  });
  const [items, setItems] = useState<Item[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, c, pr, w, u, sv] = await Promise.all([
      journalAPI.listSales(),
      customersAPI.list(),
      warehouseAPI.productsList(),
      warehouseAPI.warehousesList(),
      warehouseAPI.unitsList(),
      warehouseAPI.servicesList(),
    ]);
    if (s.success) setList(s.sales || []);
    if (c.success) setCustomers(c.customers || []);

    const opts: ProductOption[] = [];

    if (pr.success && w.success) {
      const products  = pr.products || [];
      const warehouses = w.warehouses || [];
      const units     = u.success ? (u.units || []) : [];
      products.forEach((prod: any) => {
        const unit = units.find((u: any) => u.id === prod.unit_id)?.name || "";
        warehouses.forEach((wh: any) => {
          opts.push({
            value:          `${prod.id}|${wh.id}`,
            label:          `📦 ${prod.name} — ${wh.name}`,
            product_id:     prod.id,
            product_name:   prod.name,
            warehouse_id:   wh.id,
            warehouse_name: wh.name,
            unit,
            item_type:      "product",
          });
        });
      });
    }

    if (sv.success) {
      const services = sv.services || [];
      services.forEach((svc: any) => {
        opts.push({
          value:          `svc_${svc.id}`,
          label:          `🔧 ${svc.name}`,
          product_id:     svc.id,
          product_name:   svc.name,
          warehouse_id:   0,
          warehouse_name: "",
          unit:           "",
          item_type:      "service",
        });
      });
    }

    setProductOptions(opts);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const addItem = () =>
    setItems(p => [...p, { product_id: "", product_name: "", unit: "", quantity: "1", warehouse_id: "", warehouse_name: "", unit_price: "0", total_price: 0 }]);

  const removeItem = (i: number) => setItems(p => p.filter((_, idx) => idx !== i));

  const updateItem = (i: number, key: keyof Item, val: any) => {
    setItems(p => p.map((item, idx) => {
      if (idx !== i) return item;
      const u = { ...item, [key]: val };
      if (key === "product_id") {
        const opt = productOptions.find(o => o.value === val);
        if (opt) {
          u.product_name   = opt.product_name;
          u.warehouse_id   = opt.item_type === "service" ? "" : String(opt.warehouse_id);
          u.warehouse_name = opt.item_type === "service" ? "خدمت" : opt.warehouse_name;
          u.unit           = opt.unit;
        }
      }
      if (key === "quantity" || key === "unit_price") {
        u.total_price = calcTotal(u.quantity, u.unit_price);
      }
      return u;
    }));
  };

  const totalAmount = items.reduce((s, i) => s + i.total_price, 0);
  const netAmount   = totalAmount - (parseFloat(form.discount || "0") || 0);

  const buildSubmitPayload = (statusValue: "pending"|"confirmed") => {
    const customer = customers.find((c: any) => String(c.id) === form.customer_id);
    return {
      date:              form.date,
      customer_id:       parseInt(form.customer_id),
      customer_name:     `${customer?.first_name || ""} ${customer?.last_name || ""} ${customer?.company_name || ""}`.trim(),
      customer_currency: customer?.currency || form.customer_currency,
      discount:          parseFloat(form.discount || "0"),
      items: items.map(i => {
        const isService = i.product_id.startsWith("svc_");
        const productId = isService
          ? parseInt(i.product_id.replace("svc_", ""))
          : (i.product_id ? parseInt(i.product_id.split("|")[0]) : null);
        return {
          product_id:     productId,
          product_name:   i.product_name,
          unit:           i.unit,
          quantity:       parseFloat(String(i.quantity)) || 0, // 🆕 parseFloat
          warehouse_id:   (!isService && i.warehouse_id) ? parseInt(i.warehouse_id) : null,
          warehouse_name: i.warehouse_name,
          unit_price:     parseFloat(String(i.unit_price)) || 0, // 🆕 parseFloat
          total_price:    i.total_price,
          item_type:      isService ? "service" : "product",
        };
      }),
      notes:      form.notes,
      attachment: form.attachment,
      truck_plate: form.truck_plate,
      waybill_number: form.waybill_number,
      status:     statusValue,
    };
  };

  const validateForm = () => {
    if (!form.customer_id) { showToast("مشتری الزامی است", false); return false; }
    if (items.length === 0) { showToast("حداقل یک آیتم اضافه کنید", false); return false; }
    if (items.some(i => !i.product_name || (parseFloat(String(i.quantity)) || 0) <= 0)) { showToast("اطلاعات آیتم‌ها ناقص است", false); return false; }
    return true;
  };

  const resetForm = () => {
    setForm({ date: todayGregorian(), customer_id: "", customer_currency: "USD", discount: "0", notes: "", attachment: "", truck_plate: "", waybill_number: "" });
    setItems([]);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const res = await journalAPI.addSale(buildSubmitPayload("confirmed"));
    if (res.success) {
      showToast(`فروش ${res.serial} با موفقیت ثبت شد`);
      setShowForm(false); resetForm(); load();
    } else showToast(res.message || "خطا در ثبت فروش", false);
  };

  const handleSubmitPending = async () => {
    if (!validateForm()) return;
    const res = await journalAPI.addSale(buildSubmitPayload("pending"));
    if (res.success) {
      showToast(`فروش ${res.serial} در انتظار تأیید ذخیره شد`);
      setShowForm(false); resetForm(); load();
    } else showToast(res.message || "خطا در ثبت فروش", false);
  };

  const handleConfirm = async (id: number, serial: string) => {
    if (!window.confirm(`تأیید نهایی فروش ${serial}؟\nموجودی گدام، حساب درآمد و بیلانس مشتری به‌روزرسانی می‌شود.`)) return;
    const res = await journalAPI.confirmSale(id);
    if (res.success) { showToast("فروش تأیید شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن فروش ${serial} به حالت در انتظار؟\nتأثیرات آن لغو می‌شود.`)) return;
    const res = await journalAPI.revertSale(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const openEdit = (s: any) => {
    setEditItem({
      id: s.id, date: s.date,
      customer_id: String(s.customer_id),
      customer_currency: s.customer_currency,
      discount: String(s.discount || "0"),
      notes: s.notes || "", attachment: s.attachment || "",
      truck_plate: s.truck_plate || "",
      waybill_number: s.waybill_number || "",
      status: s.status || "confirmed",
      items: (s.items || []).map((it: any) => ({
        product_id: `${it.product_id}|${it.warehouse_id}`,
        product_name: it.product_name, unit: it.unit,
        quantity: String(it.quantity), // 🆕 string
        warehouse_id: String(it.warehouse_id),
        warehouse_name: it.warehouse_name,
        unit_price: String(it.unit_price), // 🆕 string
        total_price: Number(it.total_price),
      })),
    });
  };

  const handleEdit = async () => {
    if (!editItem.customer_id) { showToast("مشتری الزامی است", false); return; }
    if (!editItem.items || editItem.items.length === 0) { showToast("حداقل یک آیتم اضافه کنید", false); return; }
    const customer = customers.find((c: any) => String(c.id) === editItem.customer_id);
    const res = await journalAPI.updateSale({
      id: editItem.id, date: editItem.date,
      customer_id: parseInt(editItem.customer_id),
      customer_name: `${customer?.first_name || ""} ${customer?.last_name || ""} ${customer?.company_name || ""}`.trim(),
      customer_currency: customer?.currency || editItem.customer_currency,
      discount: parseFloat(editItem.discount || "0"),
      items: editItem.items.map((i: any) => ({
        product_id:     i.product_id ? parseInt(String(i.product_id).split("|")[0]) : null,
        product_name:   i.product_name, unit: i.unit,
        quantity:       parseFloat(String(i.quantity)) || 0, // 🆕
        warehouse_id:   i.warehouse_id ? parseInt(i.warehouse_id) : null,
        warehouse_name: i.warehouse_name,
        unit_price:     parseFloat(String(i.unit_price)) || 0, // 🆕
        total_price:    i.total_price,
      })),
      notes: editItem.notes, attachment: editItem.attachment,
      truck_plate: editItem.truck_plate || "",
      waybill_number: editItem.waybill_number || "",
    });
    if (res.success) { showToast("فروش ویرایش شد"); setEditItem(null); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف فروش ${serial}؟`)) return;
    const res = await journalAPI.deleteSale(id);
    if (res.success) { showToast("فروش حذف شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const filtered = applyFilter(list, filter, {
    searchFields: ["serial", "customer_name", "notes"],
    dateField: "date",
    amountField: "net_amount",
    currencyField: "customer_currency",
    counterpartyField: "customer_id",
  }).filter((s: any) => {
    const st = s.status || "confirmed";
    if (statusFilter === "all") return true;
    return st === statusFilter;
  });

  const pendingCount = list.filter((s:any) => (s.status||"confirmed") === "pending").length;

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && <Toast text={toast.text} ok={toast.ok} />}

      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در فروشات... (شماره سند، نام مشتری، توضیحات)",
          showDateRange: true,
          showAmountRange: true,
          showCurrency: true,
          showCounterparty: true,
          counterpartyLabel: "مشتری",
          counterpartyOptions: customers.map((c: any) => ({
            value: String(c.id),
            label: `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""}`.trim(),
          })),
        }}
        values={filter}
        onChange={setFilter}
        resultCount={filtered.length}
        totalCount={list.length}
        T={T}
        dark={dark}
            onExport={() => {
          const rows: Record<string, any>[] = [];
          filtered.forEach((s: any) => {
            const baseRow = {
              "شماره سند": s.serial,
              "تاریخ": s.date,
              "مشتری": s.customer_name,
              "ارز": s.customer_currency,
              "تخفیف": s.discount || 0,
              "خالص کل": s.net_amount,
              "توضیحات": s.notes || "",
            };
            if (s.items && s.items.length > 0) {
              s.items.forEach((it: any) => {
                rows.push({
                  ...baseRow,
                  "نام جنس / خدمت": it.product_name || "",
                  "گدام": it.warehouse_name || "",
                  "واحد": it.unit || "",
                  "تعداد": it.quantity || 0,
                  "قیمت واحد": it.unit_price || 0,
                  "مجموع آیتم": it.total_price || 0,
                });
              });
            } else {
              rows.push({
                ...baseRow,
                "نام جنس / خدمت": "",
                "گدام": "",
                "واحد": "",
                "تعداد": "",
                "قیمت واحد": "",
                "مجموع آیتم": "",
              });
            }
          });
          exportToExcel(rows, "فروشات");
        }}
      exportLabel="دانلود Excel فروشات"
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <StatusTabs
          value={statusFilter}
          onChange={setStatusFilter}
          totalCount={list.length}
          pendingCount={pendingCount}
          T={T}
          dark={dark}
        />
        {canAdd && (
          <button onClick={() => { setShowForm(true); if (items.length === 0) addItem(); }} style={addBtnSt("#10b981")}>
            <Plus size={15} /> ثبت فروش جدید
          </button>
        )}
      </div>

      {loading ? <Loading T={T} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.length === 0 && <Empty T={T} text="هیچ فروشی ثبت نشده" />}
          {/* ردیف هدر */}
          {filtered.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "130px 220px 1fr 110px 75px 1.2fr 160px 230px",
              gap: 12,
              padding: "10px 16px",
              borderRadius: 10,
              background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
              border: `1px solid ${T.cardBorder}`,
              fontSize: 11,
              fontWeight: 700,
              color: T.subtext,
            }}>
              <div>شماره سند</div>
              <div>موتر / بارنامه</div>
              <div>مشتری</div>
              <div>تاریخ</div>
              <div style={{ textAlign: "center" as const }}>آیتم‌ها</div>
              <div>شرح</div>
              <div style={{ textAlign: "left" as const }}>مبلغ خالص</div>
              <div style={{ textAlign: "center" as const }}>عملیات</div>
            </div>
          )}
          {filtered.map((s: any) => {
            const isPending = (s.status || "confirmed") === "pending";
            return (
            <div key={s.id} style={{
              ...rowSt(dark, T),
              ...(isPending ? pendingRowStyle(dark) : {}),
              display: "grid",
              gridTemplateColumns: "130px 220px 1fr 110px 75px 1.2fr 160px 230px",
              gap: 12,
              alignItems: "center",
            }}>
              {/* ستون 1: سریال + بدج در انتظار */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                <span style={badgeSt("#10b981")}>{s.serial}</span>
                {isPending && <PendingBadge />}
              </div>

              {/* ستون 2: شماره موتر و بارنامه */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                {s.truck_plate && (
                  <span style={{
                    padding: "3px 10px", borderRadius: 7,
                    background: "rgba(59,130,246,0.1)", color: "#3b82f6",
                    fontSize: 11, fontWeight: 700, fontFamily: "monospace",
                    whiteSpace: "nowrap" as const,
                  }}>
                    🚚 {s.truck_plate}
                  </span>
                )}
                {s.waybill_number && (
                  <span style={{
                    padding: "3px 10px", borderRadius: 7,
                    background: "rgba(14,165,233,0.1)", color: "#0ea5e9",
                    fontSize: 11, fontWeight: 700, fontFamily: "monospace",
                    whiteSpace: "nowrap" as const,
                  }}>
                    📄 {s.waybill_number}
                  </span>
                )}
              </div>

              {/* ستون 3: نام مشتری */}
              <div style={{ fontWeight: 700, color: T.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>
                {s.customer_name}
              </div>

              {/* ستون 4: تاریخ */}
              <div>
                <DualDate date={s.date} fontSize={12} color={T.subtext} />
              </div>

              {/* ستون 5: تعداد آیتم */}
              <div style={{ fontSize: 12, color: T.subtext, textAlign: "center" as const }}>
                {s.items?.length || 0} آیتم
              </div>

              {/* ستون 6: شرح */}
              <div style={{ fontSize: 11, color: T.subtext, fontStyle: "italic", lineHeight: 1.5, wordBreak: "break-word" as const }}>
                {s.notes || "—"}
              </div>

              {/* ستون 7: مبلغ */}
              <div style={{ fontWeight: 800, color: "#10b981", fontSize: 14, textAlign: "left" as const, direction: "ltr" as const }}>
                {fmt(s.net_amount)} {s.customer_currency}
              </div>

              {/* ستون 8: عملیات */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                <button onClick={() => setViewItem(s)} title="مشاهده" style={iconBtnSt("#8b5cf6")}><Eye size={13} /></button>
                <button onClick={() => {
                  const cust = customers.find((c: any) => c.id === s.customer_id);
                  printSaleInvoice(s, cust, cust?.balance || 0);
                }} title="چاپ فاکتور" style={iconBtnSt("#0ea5e9")}><Printer size={13} /></button>
                {isPending && canEdit && (
                  <ConfirmIconButton onClick={() => handleConfirm(s.id, s.serial)} />
                )}
                {!isPending && canEdit && (
                  <RevertIconButton onClick={() => handleRevert(s.id, s.serial)} />
                )}
                {canEdit && (
                  <button onClick={() => openEdit(s)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>
                )}
                {canDelete && (
                  <button onClick={() => handleDelete(s.id, s.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* فرم ثبت فروش */}
      {showForm && (
        <Modal title="ثبت فروش جدید" onClose={() => setShowForm(false)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <Lbl T={T}>مشتری *</Lbl>
              <SearchableSelect
                options={customers.map((c: any) => ({ value: String(c.id), label: `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""}`.trim() + ` (${c.currency})` }))}
                value={form.customer_id}
                onChange={v => {
                  const cu = customers.find((x: any) => String(x.id) === v);
                  setForm(f => ({ ...f, customer_id: v, customer_currency: cu?.currency || "USD" }));
                }}
                placeholder="انتخاب مشتری"
              />
            </div>
            <div>
              <Lbl T={T}>تاریخ</Lbl>
              <PersianDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} />
            </div>
            <div>
              <Lbl T={T}>تخفیف</Lbl>
              <input value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))}
                placeholder="0" style={inputSt(T)} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ fontSize: 13, color: T.text }}>آیتم‌های فروش</strong>
              <button onClick={addItem} style={addBtnSt("#10b981", true)}><Plus size={13} /> افزودن آیتم</button>
            </div>
            {items.map((item, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 80px 80px 100px 36px", gap: 8, marginBottom: 10, alignItems: "end", padding: "12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc", border: `1px solid ${T.border}` }}>
                <div>
                  {i === 0 && <Lbl T={T}>محصول / خدمت — گدام</Lbl>}
                  <SearchableSelect
                    options={productOptions.map(o => ({ value: o.value, label: o.label }))}
                    value={item.product_id}
                    onChange={v => updateItem(i, "product_id", v)}
                    placeholder="انتخاب محصول و گدام"
                  />
                  {item.warehouse_name && (
                    <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>
                      ✓ گدام: {item.warehouse_name} | واحد: {item.unit}
                    </div>
                  )}
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>واحد</Lbl>}
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${T.border}`, color: T.text, fontSize: 13, minHeight: 42 }}>
                    {item.unit || "—"}
                  </div>
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>تعداد</Lbl>}
                  {/* 🆕 type=text + inputMode=decimal تا اعداد اعشاری زیر 1 آزادانه قبول شوند */}
                  <input
                    type="text"
                    inputMode="decimal"
                    value={item.quantity}
                    onChange={e => updateItem(i, "quantity", e.target.value)}
                    placeholder="0.0"
                    style={{ ...inputSt(T), direction: "ltr" as const, textAlign: "right" as const }}
                  />
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>قیمت واحد</Lbl>}
                  <input
                    type="text"
                    inputMode="decimal"
                    value={item.unit_price}
                    onChange={e => updateItem(i, "unit_price", e.target.value)}
                    placeholder="0"
                    style={{ ...inputSt(T), direction: "ltr" as const, textAlign: "right" as const }}
                  />
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>مجموع</Lbl>}
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(16,185,129,0.1)" : "#f0fdf4", color: "#10b981", fontWeight: 700, fontSize: 13 }}>
                    {fmt(item.total_price)}
                  </div>
                </div>
                <div style={{ marginTop: i === 0 ? 22 : 0 }}>
                  <button onClick={() => removeItem(i)} style={{ ...iconBtnSt("#ef4444"), width: 36, height: 36 }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: dark ? "rgba(16,185,129,0.08)" : "#f0fdf4", borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", gap: 28, flexWrap: "wrap" }}>
            <div><span style={{ fontSize: 12, color: T.subtext }}>جمع کل: </span><strong style={{ color: T.text }}>{fmt(totalAmount)}</strong></div>
            <div><span style={{ fontSize: 12, color: T.subtext }}>تخفیف: </span><strong style={{ color: "#ef4444" }}>{fmt(parseFloat(form.discount || "0"))}</strong></div>
            <div><span style={{ fontSize: 12, color: T.subtext }}>خالص: </span><strong style={{ color: "#10b981", fontSize: 16 }}>{fmt(netAmount)} {form.customer_currency}</strong></div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <Lbl T={T}>توضیحات</Lbl>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} />
            </div>
            <div>
              <Lbl T={T}>🚚 شماره موتر</Lbl>
              <input value={form.truck_plate} onChange={e => setForm(f => ({ ...f, truck_plate: e.target.value }))} placeholder="مثلاً: 12345" style={{...inputSt(T), direction: "ltr" as const}} />
            </div>
            <div>
              <Lbl T={T}>📄 شماره بارنامه</Lbl>
              <input value={form.waybill_number} onChange={e => setForm(f => ({ ...f, waybill_number: e.target.value }))} placeholder="شماره بارنامه..." style={{...inputSt(T), direction: "ltr" as const}} />
            </div>
            <div>
              <Lbl T={T}>ضمیمه</Lbl>
              <input value={form.attachment} onChange={e => setForm(f => ({ ...f, attachment: e.target.value }))} placeholder="لینک یا نام فایل..." style={inputSt(T)} />
            </div>
          </div>

          <div style={{
            display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center",
            marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`,
            flexWrap: "wrap"
          }}>
            <PendingHint T={T} />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={() => setShowForm(false)} T={T} />
              <PendingSubmitButton
                onClick={() => submit(handleSubmitPending)}
                loading={submitting}
                label="ذخیره فروش در انتظار"
              />
              <ConfirmSubmitButton
                onClick={() => submit(handleSubmit)}
                loading={submitting}
                color="#10b981"
                label="ثبت فروش"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* فرم ویرایش فروش */}
      {editItem && (
        <Modal title={`ویرایش فروش${editItem.status==="pending" ? " (در انتظار)" : ""}`} onClose={() => setEditItem(null)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <Lbl T={T}>مشتری *</Lbl>
              <SearchableSelect
                options={customers.map((c: any) => ({ value: String(c.id), label: `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""}`.trim() + ` (${c.currency})` }))}
                value={editItem.customer_id}
                onChange={v => { const cu = customers.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, customer_id: v, customer_currency: cu?.currency || "USD" })); }}
                placeholder="انتخاب مشتری"
              />
            </div>
            <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={editItem.date} onChange={v => setEditItem((f: any) => ({ ...f, date: v }))} /></div>
            <div><Lbl T={T}>تخفیف</Lbl><input value={editItem.discount} onChange={e => setEditItem((f: any) => ({ ...f, discount: e.target.value }))} placeholder="0" style={inputSt(T)} /></div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ fontSize: 13, color: T.text }}>آیتم‌ها</strong>
              <button onClick={() => setEditItem((f: any) => ({ ...f, items: [...f.items, { product_id: "", product_name: "", unit: "", quantity: "1", warehouse_id: "", warehouse_name: "", unit_price: "0", total_price: 0 }] }))} style={addBtnSt("#10b981", true)}><Plus size={13} /> افزودن آیتم</button>
            </div>
            {editItem.items.map((item: any, i: number) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 80px 80px 100px 36px", gap: 8, marginBottom: 10, alignItems: "end", padding: "12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc", border: `1px solid ${T.border}` }}>
                <div>
                  {i === 0 && <Lbl T={T}>محصول / گدام</Lbl>}
                  <SearchableSelect
                    options={productOptions.map(o => ({ value: o.value, label: o.label }))}
                    value={item.product_id}
                    onChange={v => { const opt = productOptions.find(o => o.value === v); setEditItem((f: any) => ({ ...f, items: f.items.map((it: any, idx: number) => idx === i ? { ...it, product_id: v, product_name: opt?.product_name || "", warehouse_id: String(opt?.warehouse_id || ""), warehouse_name: opt?.warehouse_name || "", unit: opt?.unit || "", total_price: calcTotal(it.quantity, it.unit_price) } : it) })); }}
                    placeholder="انتخاب محصول و گدام"
                  />
                  {item.warehouse_name && <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>✓ گدام: {item.warehouse_name} | واحد: {item.unit}</div>}
                </div>
                <div>{i === 0 && <Lbl T={T}>واحد</Lbl>}<div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${T.border}`, color: T.text, fontSize: 13, minHeight: 42 }}>{item.unit || "—"}</div></div>
                <div>
                  {i === 0 && <Lbl T={T}>تعداد</Lbl>}
                  {/* 🆕 type=text + inputMode=decimal */}
                  <input
                    type="text"
                    inputMode="decimal"
                    value={item.quantity}
                    onChange={e => setEditItem((f: any) => ({ ...f, items: f.items.map((it: any, idx: number) => idx === i ? { ...it, quantity: e.target.value, total_price: calcTotal(e.target.value, it.unit_price) } : it) }))}
                    placeholder="0.0"
                    style={{ ...inputSt(T), direction: "ltr" as const, textAlign: "right" as const }}
                  />
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>قیمت واحد</Lbl>}
                  <input
                    type="text"
                    inputMode="decimal"
                    value={item.unit_price}
                    onChange={e => setEditItem((f: any) => ({ ...f, items: f.items.map((it: any, idx: number) => idx === i ? { ...it, unit_price: e.target.value, total_price: calcTotal(it.quantity, e.target.value) } : it) }))}
                    placeholder="0"
                    style={{ ...inputSt(T), direction: "ltr" as const, textAlign: "right" as const }}
                  />
                </div>
                <div>{i === 0 && <Lbl T={T}>مجموع</Lbl>}<div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(16,185,129,0.1)" : "#f0fdf4", color: "#10b981", fontWeight: 700, fontSize: 13 }}>{fmt(item.total_price)}</div></div>
                <div style={{ marginTop: i === 0 ? 22 : 0 }}><button onClick={() => setEditItem((f: any) => ({ ...f, items: f.items.filter((_: any, idx: number) => idx !== i) }))} style={{ ...iconBtnSt("#ef4444"), width: 36, height: 36 }}><Trash2 size={13} /></button></div>
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div><Lbl T={T}>توضیحات</Lbl><input value={editItem.notes} onChange={e => setEditItem((f: any) => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>🚚 شماره موتر</Lbl><input value={editItem.truck_plate || ""} onChange={e => setEditItem((f: any) => ({ ...f, truck_plate: e.target.value }))} placeholder="مثلاً: 12345" style={{...inputSt(T), direction: "ltr" as const}} /></div>
            <div><Lbl T={T}>📄 شماره بارنامه</Lbl><input value={editItem.waybill_number || ""} onChange={e => setEditItem((f: any) => ({ ...f, waybill_number: e.target.value }))} placeholder="شماره بارنامه..." style={{...inputSt(T), direction: "ltr" as const}} /></div>
            <div><Lbl T={T}>ضمیمه</Lbl><input value={editItem.attachment} onChange={e => setEditItem((f: any) => ({ ...f, attachment: e.target.value }))} placeholder="لینک..." style={inputSt(T)} /></div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <BtnCancel onClick={() => setEditItem(null)} T={T} />
            <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#3b82f6" label="ذخیره ویرایش" />
          </div>
        </Modal>
      )}

      {viewItem && (
        <Modal title={`جزئیات فروش: ${viewItem.serial}`} onClose={() => setViewItem(null)} dark={dark} T={T}>
          <div id="print-sale">
            <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: T.subtext }}>مشتری: <strong style={{ color: T.text }}>{viewItem.customer_name}</strong></span>
              <span style={{ fontSize: 12, color: T.subtext, display: "inline-flex", alignItems: "center", gap: 6 }}>تاریخ: <DualDate date={viewItem.date} fontSize={12} color={T.text} inline /></span>
              <span style={{ fontSize: 12, color: T.subtext }}>خالص: <strong style={{ color: "#10b981" }}>{fmt(viewItem.net_amount)} {viewItem.customer_currency}</strong></span>
              {viewItem.truck_plate && <span style={{ fontSize: 12, color: T.subtext }}>🚚 شماره موتر: <strong style={{ color: "#3b82f6" }}>{viewItem.truck_plate}</strong></span>}
              {viewItem.waybill_number && <span style={{ fontSize: 12, color: T.subtext }}>📄 شماره بارنامه: <strong style={{ color: "#0ea5e9" }}>{viewItem.waybill_number}</strong></span>}
              {viewItem.notes && <span style={{ fontSize: 12, color: T.subtext }}>توضیح: <strong style={{ color: T.text }}>{viewItem.notes}</strong></span>}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: dark ? "rgba(255,255,255,0.05)" : "#f8fafc" }}>
                  {["محصول", "واحد", "تعداد", "گدام", "قیمت واحد", "مجموع"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: T.subtext, borderBottom: `1px solid ${T.cardBorder}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {viewItem.items?.map((it: any, idx: number) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.product_name}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.unit}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.quantity}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.warehouse_name}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{fmt(it.unit_price)}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#10b981" }}>{fmt(it.total_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button onClick={() => {
              const cust = customers.find((c: any) => c.id === viewItem.customer_id);
              printSaleInvoice(viewItem, cust, cust?.balance || 0);
            }} style={addBtnSt("#8b5cf6")}>
              <Printer size={14} /> چاپ فاکتور
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}